import React, { useEffect, useState } from 'react';
import useFetch from 'use-http';
import { BiDownload } from 'react-icons/bi';
import { Loader2, X } from 'src/components/common/icons';
import { Field, SelectField } from './Master';

interface ILeaseInvoiceReportDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

const INVOICE_TYPE_OPTIONS = [
    { value: 'A', label: 'All Invoices' },
    { value: 'M', label: 'Main Invoices' },
    { value: 'S', label: 'Sub-Invoices Only' },
];

const ORDER_BY_OPTIONS = [
    { value: '1', label: 'Invoice Number' },
    { value: '2', label: 'Client Number, Invoice Number' },
];

const formatDateInput = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
};

const getToday = (): string => formatDateInput(new Date());
const getFirstDayOfYear = (): string => `${new Date().getFullYear()}-01-01`;

const formatDateForReport = (value: string): string => {
    const parts = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return parts ? `${parts[2]}/${parts[3]}/${parts[1]}` : value;
};

const isValidNumberList = (value: string): boolean => !value.trim() || /^\d+(\s*,\s*\d+)*$/.test(value.trim());
const normalizeNumberList = (value: string): string =>
    value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
        .join(',');

const LeaseInvoiceReportDialog: React.FC<ILeaseInvoiceReportDialogProps> = ({ isOpen, onClose }) => {
    const { get, response } = useFetch();
    const [clientNo, setClientNo] = useState('');
    const [projectNo, setProjectNo] = useState('');
    const [beginningDate, setBeginningDate] = useState(getFirstDayOfYear);
    const [endingDate, setEndingDate] = useState(getToday);
    const [invoiceType, setInvoiceType] = useState('A');
    const [orderBy, setOrderBy] = useState('1');
    const [isGenerating, setIsGenerating] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        setMessage('');
        const handleKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape' && !isGenerating) {
                onClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isGenerating, isOpen, onClose]);

    const handleDownload = async (): Promise<void> => {
        if (!beginningDate || !endingDate) {
            setMessage('Beginning Invoice Date and Ending Invoice Date are required.');
            return;
        }
        if (beginningDate > endingDate) {
            setMessage('Beginning Invoice Date cannot be after Ending Invoice Date.');
            return;
        }
        if (!isValidNumberList(clientNo) || !isValidNumberList(projectNo)) {
            setMessage('Client and Project Numbers must be comma-separated numbers.');
            return;
        }

        setIsGenerating(true);
        setMessage('');

        try {
            const params = new URLSearchParams();
            params.append('clientNo', normalizeNumberList(clientNo));
            params.append('projectNo', normalizeNumberList(projectNo));
            params.append('beginningDate', formatDateForReport(beginningDate));
            params.append('endingDate', formatDateForReport(endingDate));
            params.append('orderBy', orderBy);
            params.append('invoiceType', invoiceType);

            await get(`/v1.0/leases/admin/lease-invoice/report?${params.toString()}`);
            if (!response.ok) {
                setMessage('The Lease Invoice report could not be generated.');
                return;
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `LeaseInvoice_${getToday().replace(/-/g, '')}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch {
            setMessage('The Lease Invoice report could not be generated.');
        } finally {
            setIsGenerating(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div style={dialogStyles.overlay} onClick={isGenerating ? undefined : onClose}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="lease-invoice-dialog-title"
                style={dialogStyles.content}
                onClick={(event) => event.stopPropagation()}
            >
                <div style={dialogStyles.header}>
                    <h2 id="lease-invoice-dialog-title" style={dialogStyles.title}>
                        Lease Invoice
                    </h2>
                    <button type="button" onClick={onClose} style={dialogStyles.closeButton} aria-label="Close Lease Invoice" disabled={isGenerating}>
                        <X style={{ width: '1.1rem', height: '1.1rem' }} />
                    </button>
                </div>

                <div style={dialogStyles.body}>
                    <div style={dialogStyles.fieldsGrid}>
                        <Field label="Client Number(s)" value={clientNo} editable={true} onChange={(value) => setClientNo(value || '')} />
                        <Field label="Project Number(s)" value={projectNo} editable={true} onChange={(value) => setProjectNo(value || '')} />
                        <Field
                            label="Beginning Invoice Date"
                            type="date"
                            value={beginningDate}
                            editable={true}
                            onChange={(value) => setBeginningDate(value || '')}
                        />
                        <Field
                            label="Ending Invoice Date"
                            type="date"
                            value={endingDate}
                            editable={true}
                            onChange={(value) => setEndingDate(value || '')}
                        />
                        <SelectField
                            label="Invoice Type"
                            value={invoiceType}
                            editable={true}
                            options={INVOICE_TYPE_OPTIONS}
                            onChange={(value) => setInvoiceType(value || 'A')}
                        />
                        <SelectField
                            label="Order By"
                            value={orderBy}
                            editable={true}
                            options={ORDER_BY_OPTIONS}
                            onChange={(value) => setOrderBy(value || '1')}
                        />
                    </div>

                    {message && (
                        <div role="alert" style={dialogStyles.message}>
                            {message}
                        </div>
                    )}
                </div>

                <div style={dialogStyles.footer}>
                    <button type="button" onClick={onClose} style={dialogStyles.secondaryButton} disabled={isGenerating}>
                        Cancel
                    </button>
                    <button type="button" onClick={handleDownload} style={dialogStyles.primaryButton} disabled={isGenerating}>
                        <span style={dialogStyles.buttonInner}>
                            {isGenerating ? (
                                <Loader2 className="animate-spin" style={{ width: '1rem', height: '1rem' }} />
                            ) : (
                                <BiDownload aria-hidden="true" style={{ width: '1rem', height: '1rem' }} />
                            )}
                            {isGenerating ? 'Generating...' : 'Download Excel'}
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
};

const dialogStyles: { [key: string]: React.CSSProperties } = {
    overlay: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.58)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
    },
    content: {
        width: 'min(640px, 96vw)',
        maxHeight: '92vh',
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        boxShadow: '0 24px 56px rgba(15, 23, 42, 0.28)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    header: {
        minHeight: '58px',
        padding: '0.85rem 1rem',
        borderBottom: '1px solid #E2E8F0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
    },
    title: { margin: 0, color: '#0F172A', fontSize: '1.05rem', fontWeight: 700 },
    closeButton: {
        width: '2rem',
        height: '2rem',
        padding: 0,
        border: 'none',
        borderRadius: '6px',
        backgroundColor: 'transparent',
        color: '#64748B',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    body: { padding: '1rem', overflowY: 'auto' },
    fieldsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '0.8rem 1rem',
        alignItems: 'end',
    },
    message: {
        marginTop: '0.9rem',
        padding: '0.65rem 0.75rem',
        border: '1px solid #FCA5A5',
        borderRadius: '6px',
        backgroundColor: '#FEF2F2',
        color: '#991B1B',
        fontSize: '0.8rem',
    },
    footer: {
        padding: '0.8rem 1rem',
        borderTop: '1px solid #E2E8F0',
        backgroundColor: '#F8FAFC',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '0.65rem',
    },
    buttonInner: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' },
    primaryButton: {
        minHeight: '34px',
        padding: '0.45rem 0.85rem',
        border: 'none',
        borderRadius: '6px',
        backgroundColor: '#1E40AF',
        color: '#FFFFFF',
        cursor: 'pointer',
        fontWeight: 600,
    },
    secondaryButton: {
        minHeight: '34px',
        padding: '0.45rem 0.85rem',
        border: '1px solid #CBD5E1',
        borderRadius: '6px',
        backgroundColor: '#FFFFFF',
        color: '#0F172A',
        cursor: 'pointer',
        fontWeight: 600,
    },
};

export default LeaseInvoiceReportDialog;



// WEBPACK FOOTER //
// ./src/components/leases/sections/LeaseInvoiceReportDialog.tsx